/**
 * RPC protocol contracts and DTOs for the Product Price Scanner Mini App.
 * Self-contained local types (decoupled from @fit/contracts).
 */

export type ScanCapability = "product.resolveBarcode" | "price.compare";
export type ScannerRpcMethod = ScanCapability | "product.favorite" | "scan.recordHistory" | "priceTag.resolve" | "scan.recordDebug";

export type RpcErrorCode =
  | "INVALID_REQUEST"
  | "UNAUTHORIZED"
  | "SESSION_EXPIRED"
  | "RATE_LIMITED"
  | "METHOD_NOT_ALLOWED"
  | "INVALID_BARCODE"
  | "PRODUCT_NOT_FOUND"
  | "NO_PRICE_DATA"
  | "AGENT_UNAVAILABLE"
  | "INTERNAL_ERROR"
  | "TIMEOUT"
  | "NO_TEXT_DETECTED"
  | "PRODUCT_NOT_MATCHED";

export interface RpcError {
  code: RpcErrorCode;
  message: string;
}

export interface RpcRequest<TParams = unknown> {
  id: string;
  method: ScannerRpcMethod;
  params: TParams;
}

export type RpcResponse<TResult = unknown> =
  | { id: string; result: TResult; error?: never }
  | { id: string; error: RpcError; result?: never };

export type ScanSessionStatus = "created" | "connected" | "expired" | "closed";

export interface ScanSession {
  id: string;
  telegramUserId: string;
  capabilities: ScanCapability[];
  createdAt: number;
  expiresAt: number;
  maxRequests: number;
  status: ScanSessionStatus;
}

export interface ScannedProduct {
  id: string;
  name: string;
  barcode: string;
  /** 80×80 JPEG data URL from local-agent; optional, never a public CDN URL. */
  thumbnail?: string;
}

export interface ResolveBarcodeRequest {
  barcode: string;
}

export interface ResolveBarcodeResponse {
  product: ScannedProduct;
}

export interface StorePrice {
  storeId: string;
  storeName: string;
  price: number;
  currency: string;
}

export interface BestPrice {
  storeId: string;
  price: number;
}

export interface PriceCompareRequest {
  barcode: string;
  currentStoreId?: string;
}

export interface PriceCompareResponse {
  product: ScannedProduct;
  prices: StorePrice[];
  best: BestPrice;
  favorited?: boolean;
  /** True when the EAN is valid but the catalog has no product yet. */
  missing?: boolean;
}

export function missingCatalogResult(barcode: string, name?: string): PriceCompareResponse {
  return {
    product: { id: "", name: name?.trim() || barcode, barcode },
    prices: [],
    best: { storeId: "", price: 0 },
    missing: true,
  };
}

export type PriceTagSizeUnit = "g" | "kg" | "ml" | "l" | "pcs";

export interface PriceTagParsedFields {
  brand?: string;
  productName?: string;
  size?: { value: number; unit: PriceTagSizeUnit };
  regularPrice?: number;
  promoPrice?: number;
  loyaltyPrice?: number;
  unitPrice?: { value: number; unit: "kg" | "l" | "pcs" };
  loyaltyProgram?: string;
  internalProductCode?: string;
}

export interface ParsedPriceTag extends PriceTagParsedFields {
  barcode?: string;
  rawText: string;
}

export interface PriceTagProductCandidate {
  productId: string;
  name: string;
  brand?: string;
  size?: string;
  ean?: string;
  confidence: number;
}

export interface PriceTagResolveRequest {
  storeId?: string;
  rawText: string;
  barcode?: string;
  parsed: PriceTagParsedFields;
  clientConfidence?: number;
  productId?: string;
}

export interface PriceTagResolveResponse {
  recognition: {
    method: "barcode" | "ocr";
    confidence: number;
  };
  rawText: string;
  barcode?: string;
  parsed: PriceTagParsedFields;
  product?: {
    id: string;
    name: string;
    brand?: string;
    ean?: string;
    eanSource?: "price_tag" | "catalog";
  };
  alternatives?: PriceTagProductCandidate[];
  comparison?: PriceCompareResponse;
}

export interface FavoriteProductResponse {
  added: boolean;
  favorited: boolean;
}

export interface OfflineScanItem {
  id: string;
  barcode: string;
  timestamp: number;
  storeId?: string;
}

export const SCANNER_PROTOCOL_VERSION = 1;

export interface ProtocolHello {
  type: "hello";
  protocol: number;
}
